import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import type { ScreenWords } from '../clients/api';

export interface Articles { execution: number; goods_sale: number; adjustment: number }
export interface Suspicion { date: string | null; reason_key: string }
export interface PayrollLine {
  date: string; procedure: string | null; base_kopeks: number; rate_kind: 'percent' | 'fix' | null;
  rate_bps: number | null; fixed_kopeks: number | null; accrued_kopeks: number;
  fact_kind: string; reason_key: string; suspicion_keys: string[];
  /** `s1027` C-13: строка уже проведена в прежней системе — в «к выплате» не входит. */
  archive?: boolean;
}
export interface PayrollVisit {
  key: string; time: string | null; customer_name: string | null; staff_name: string | null;
  total_kopeks: number; lines: PayrollLine[];
}
export interface PayrollDay { date: string; total_kopeks: number; visits: PayrollVisit[] }
export interface PayrollRow {
  staff_id: number; name: string | null; accrued_kopeks: number; by_kind: Articles;
  /** `s1027` C-13: к выплате (только наши строки) и уже проведённое ранее. Старый сервер их не шлёт. */
  payable_kopeks?: number | null; archive_kopeks?: number | null;
  suspicions: number; suspicion_items: Suspicion[];
}
export interface ArchiveDocument {
  document_id: number; staff_id: number | null; staff_name?: string | null;
  from: string; to: string; total_kopeks: number; lines: PayrollLine[];
}
/** `s966`: `half` — the half-month statement, `monthly` — master percents of a month, payable on the 10th. */
export type PayrollPeriod = 'half' | 'monthly';
export interface PayrollData {
  kind?: PayrollPeriod; payable_on?: string | null;
  from: string; to: string; status: 'preview' | 'draft' | 'confirmed' | 'vendor_confirmed' | 'not_formed';
  scope_required?: boolean; preview_sha256?: string | null; can_form?: boolean;
  rows?: PayrollRow[]; lines?: PayrollLine[]; days?: PayrollDay[]; staff_id?: number; staff_name?: string | null; can_view_settlement?: boolean;
  by_kind: Articles; total_kopeks?: number | null;
  /** `s1027` C-13: итог к выплате (только наши строки) и уже проведённое ранее; `total_kopeks` — прежний общий. */
  total_payable_kopeks?: number | null; total_archive_kopeks?: number | null;
  archive_documents?: ArchiveDocument[]; documents?: ArchiveDocument[];
  words: ScreenWords; hints: Record<string, string | null>;
}
export const payrollKey = (kind: string, filial: number | null, date: string, staff?: number, period: PayrollPeriod = 'half') =>
  (period === 'monthly' ? ['master-payroll', kind, filial, date, staff ?? null, period] : ['master-payroll', kind, filial, date, staff ?? null]) as readonly unknown[];
const headers = (filial: number | null) => ({ 'X-Filial-Id': filial === null ? 'all' : String(filial) });
export async function getPayroll(kind: 'period' | 'statement' | 'my-statement', filial: number | null, date: string, staff?: number, period?: PayrollPeriod): Promise<PayrollData> {
  return (await apiClient.get(`/salary/masters/${kind}`, { params: { date, staff_id: staff, ...(period === 'monthly' ? { kind: period } : {}) }, headers: headers(filial) })).data.data;
}
export async function formPayroll(input: { filial: number; date: string; preview_sha256: string; kind?: PayrollPeriod }): Promise<PayrollData> {
  return (await apiClient.post('/salary/masters/form', { date: input.date, preview_sha256: input.preview_sha256, ...(input.kind === 'monthly' ? { kind: input.kind } : {}) }, { headers: headers(input.filial) })).data.data;
}
export function usePayrollWords(enabled = true) {
  return useQuery({ queryKey: ['master-payroll-words'], enabled,
    queryFn: async () => (await apiClient.get<{ data: { words: ScreenWords; hints: Record<string, string | null> } }>('/salary/masters/words')).data.data,
    staleTime: 60_000 });
}

export interface PayrollRule {
  id: number; match_kind: 'native_procedure' | 'procedure' | 'native_category' | 'category' | 'service' | 'good';
  target_label: string; vendor_category: string | null; procedure_id: number | null; external_item_id: number | null;
  vendor_company_id: number; branch_name: string | null; rate_kind: 'percent' | 'fix';
  rate_bps: number | null; fixed_kopeks: number | null; valid_from: string; valid_to: string | null;
}
export interface PayrollRuleSet {
  id: number; code: string; title: string; kind: string; historical_only: boolean;
  scope_kind: 'network' | 'filial' | 'staff'; scope_staff_name: string | null; scope_filial_name: string | null;
  rules: PayrollRule[];
}
export interface PayrollRulesData {
  today: string; can_edit: boolean; categories: string[]; procedures: { id: number; name: string }[]; sets: PayrollRuleSet[];
  words: ScreenWords; hints: Record<string, string | null>;
}
export interface PayrollRuleInput {
  rate_kind: 'percent' | 'fix'; rate_bps?: number; fixed_kopeks?: number; valid_from: string;
}
export interface PayrollRuleCreateInput extends PayrollRuleInput {
  pay_rule_set_id: number; match_kind: 'native_procedure' | 'native_category' | 'service' | 'good';
  procedure_id?: number; vendor_category?: string; external_item_id?: number; vendor_company_id: number;
}
export async function getPayrollRules(): Promise<PayrollRulesData> {
  const response = await apiClient.get('/salary/masters/rules');
  const data = response.data.data;
  const words = await apiClient.get('/salary/masters/words');
  return { ...data, words: words.data.data.words, hints: words.data.data.hints };
}
export async function createPayrollRule(input: PayrollRuleCreateInput): Promise<PayrollRulesData> {
  const response = await apiClient.post('/salary/masters/rules', input);
  const words = await apiClient.get('/salary/masters/words');
  return { ...response.data.data, words: words.data.data.words, hints: words.data.data.hints };
}
export async function versionPayrollRule(id: number, input: PayrollRuleInput): Promise<PayrollRulesData> {
  const response = await apiClient.post(`/salary/masters/rules/${id}/version`, input);
  const words = await apiClient.get('/salary/masters/words');
  return { ...response.data.data, words: words.data.data.words, hints: words.data.data.hints };
}
export async function retirePayrollRule(id: number, valid_to: string): Promise<PayrollRulesData> {
  const response = await apiClient.post(`/salary/masters/rules/${id}/retire`, { valid_to });
  const words = await apiClient.get('/salary/masters/words');
  return { ...response.data.data, words: words.data.data.words, hints: words.data.data.hints };
}

/** Calendar navigation only. Monetary sums and article classification come from the server. */
export function shiftHalfMonth(date: string, direction: -1 | 1): string {
  const [year, month, day] = date.split('-').map(Number);
  const next = direction === 1
    ? new Date(Date.UTC(year, day <= 15 ? month - 1 : month, day <= 15 ? 16 : 1))
    : new Date(Date.UTC(year, day <= 15 ? month - 2 : month - 1, day <= 15 ? 16 : 1));
  return next.toISOString().slice(0, 10);
}
export function shiftMonth(date: string, direction: -1 | 1): string {
  const [year, month] = date.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1 + direction, 1)).toISOString().slice(0, 10);
}
export function periodLabel(from: string, to: string): string {
  const first = new Date(from.slice(0, 10) + 'T12:00:00Z');
  const last = new Date(to.slice(0, 10) + 'T12:00:00Z');
  if (from.slice(0, 7) !== to.slice(0, 7)) {
    const firstYear = first.getUTCFullYear();
    return `${dayLabel(from)}${firstYear !== last.getUTCFullYear() ? ` ${firstYear}` : ''}–${dayLabel(to)} ${last.getUTCFullYear()}`;
  }
  const month = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', timeZone: 'UTC' }).formatToParts(last).find(part => part.type === 'month')?.value ?? '';
  return `${first.getUTCDate()}–${last.getUTCDate()} ${month} ${last.getUTCFullYear()}`;
}
export const dayLabel = (date: string) => new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', timeZone: 'UTC' }).format(new Date(date.slice(0, 10) + 'T12:00:00Z'));
